import React from 'react'
import moment from 'moment'
import { suggestionsOptionsApiService } from '../api/staticOptionsServices'
import DateFilterDropdown from '../../components/DateFilterDropdown/DateFilterDropdown'
import RangeFilterDropdown from '../../components/RangeFilterDropdown/RangeFilterDropdown'
import { getClientOptions } from '../getOptions'
import {
  booksNameFields,
  DATE_FORMATS,
  requiredFieldsForPrediction,
} from '../constants/constant'
import VerifiedIcon from '../../assets/icons/VerifiedIcon'
import { addDays, eachMonthOfInterval, format, isAfter } from 'date-fns'
import { Tooltip } from 'antd'

export const filteredOptions = (field, Options, naOption) => {
  if (['csmEmail', 'accountantEmail'].includes(naOption)) {
    const filterOptions =
      Options?.length > 0
        ? Options?.map((option) => {
            return {
              [field]: option?.name,
              value: option?.email,
            }
          })
        : []
    filterOptions.push({ [field]: 'N/A', value: null })
    return filterOptions
  } else {
    const filterOptions =
      Options?.length > 0
        ? Options?.map((option) => {
            return {
              [field]: option,
              value: option,
            }
          })
        : []
    Boolean(naOption || naOption === null) &&
      filterOptions.push({ [field]: 'N/A', value: naOption })
    return filterOptions
  }
}

export const getHeaderOptions = (commonHeaders) => {
  const headerOptions = commonHeaders?.map((item, index) => {
    const path = item.path
    const key = item.key
    return {
      path,
      key,
      label: item.label,
      disabled: item?.disabled || false,
    }
  })
  return headerOptions
}
export const getSideBarOptions = (userRole, commonSideBarOptions) => {
  const accountantSidebarOptions = commonSideBarOptions.filter(
    (item) => item.accountant
  )
  const csmSidebarOptions = commonSideBarOptions.filter((item) => item.csm)
  const pmSidebarOptions = commonSideBarOptions.filter((item) => item.pm)

  let options
  switch (userRole) {
    case 'ROLE_ACCOUNTANT':
      options = accountantSidebarOptions
      break
    case 'ROLE_CSM':
      options = csmSidebarOptions
      break
    case 'ROLE_PM':
      options = pmSidebarOptions
      break
    case 'ROLE_ADMIN':
      options = commonSideBarOptions.filter((item) => item.admin !== false)
      break
    default:
      // No recognized role (e.g. logged out / session expired) -- fail
      // closed instead of falling through to the admin-equivalent list.
      options = []
      break
  }

  const sidebarOptions = options.map((item, index) => {
    const key = item.key
    const path = item.path
    return {
      path,
      key: `${key}`,
      icon: React.createElement(item.icon),
      label: item.label,
    }
  })
  return sidebarOptions
}
export function getCurrentRoleRoutes(routes, currentUserRole) {
  const accountantAccessibleRoutesArray = [
    'loginpage',
    'forgotpassword',
    'setpassword',
    'resetpassword',
    'projectlist',
    'projectdetails',
    'predictionlist',
    'predictiondetails',
    'dashboard',
    'invoicedetails',
    'existinginvoice',
    'existingprediction',
    'clientdetails',
    'companylist',
    'companydetails',
    'useractivity',
    'ratedetails',
    'todolist',
    'leaveBalance',
    'projectNotes',
    'incentive',
    'revenuelist',
    'serverLogs',
    'invoiceDashboard',
    'rateMaster',
    'deliveryProgressDetails',
    'estimationHistoryDetails',
    'anomalyDashboard',
  ]
  const csmNotAccessibleRoutesArray = [
    'userlist',
    'jobScheduler',
    'salesPersons',
    'identityProvider',
    'projectdelivery',
    'deliverySettings',
  ]
  const pmAccessibleRoutesArray = [
    'loginpage',
    'forgotpassword',
    'setpassword',
    'resetpassword',
    'projectdelivery',
    'projectdetails',
    'leaveBalance',
    'deliveryProgressDetails',
    'estimationHistoryDetails',
    // Added so PM's Billing Gap anomaly rows (ROLE_SCOPE in useAnomalies.js)
    // have somewhere to deep-link to -- their Action column routes here.
    'predictiondetails',
  ]
  switch (currentUserRole) {
    case 'ROLE_ADMIN':
      // Project Delivery is PM-only by design (see Sidebar.jsx's `admin: false`
      // flag on this item) -- everything else stays unfiltered for Admin.
      return routes.filter((routeItem) => routeItem[0] !== 'projectdelivery')

    case 'ROLE_CSM':
      const csmRoutes = []
      routes.forEach((routeItem) => {
        if (!csmNotAccessibleRoutesArray.includes(routeItem[0])) {
          csmRoutes.push(routeItem)
        }
      })
      return csmRoutes

    case 'ROLE_ACCOUNTANT':
      const accountantRoutes = []
      routes.forEach((routeItem) => {
        accountantAccessibleRoutesArray.forEach((requiredItem) => {
          if (routeItem[0] === requiredItem) {
            accountantRoutes.push(routeItem)
          }
        })
      })
      return accountantRoutes

    case 'ROLE_PM':
      const pmRoutes = []
      routes.forEach((routeItem) => {
        if (pmAccessibleRoutesArray.includes(routeItem[0])) {
          pmRoutes.push(routeItem)
        }
      })
      return pmRoutes
    default:
      // Unknown/missing/malformed role (logged out, session invalid, or an
      // unrecognized role value) -- fail closed to only the public routes
      // instead of exposing every protected route in the Switch. Mirrors
      // the fail-closed default in getSideBarOptions above.
      return routes.filter((routeItem) => routeItem[1]?.secure === false)
  }
}

export const getColumnSearchProps = (
  dataIndex,
  dateFrmt,
  picker,
  isRevenueTab = false,
  defaultFilteredValue,
  isNaOption = false
) => ({
  filterDropdown: ({
    setSelectedKeys,
    selectedKeys,
    confirm,
    clearFilters,
  }) => (
    <DateFilterDropdown
      setSelectedKeys={setSelectedKeys}
      selectedKeys={selectedKeys}
      confirm={confirm}
      clearFilters={clearFilters}
      dataIndex={dataIndex}
      dateFrmt={dateFrmt}
      picker={picker}
      defaultFilteredValue={defaultFilteredValue}
      isRevenueTab={isRevenueTab}
      isNaOption={isNaOption}
    />
  ),
  onFilter: isRevenueTab
    ? undefined
    : (value, record) => {
        return record[dataIndex]
          ? moment(record[dataIndex]).format(dateFrmt) === value
          : false
      },
  filteredValue: defaultFilteredValue,
})

export const updateCompanyBasedOptions = async () => {
  const companyOptions = await suggestionsOptionsApiService('company')
  const clientNameOptions = await getClientOptions()
  const companyCountry = await suggestionsOptionsApiService('companyCountry')
  const companyState = await suggestionsOptionsApiService('companyState')
  const companyCity = await suggestionsOptionsApiService('companyCity')
  const displayNameOptions = await suggestionsOptionsApiService('displayName')
  return {
    companyOptions,
    clientNameOptions,
    companyCountry,
    companyState,
    companyCity,
    displayNameOptions,
  }
}
export const renderFeaturedElement = (Options) => {
  return Options?.map((option) => {
    return {
      value: option.email,
      label: `${option?.name} | ${option?.email}`,
    }
  })
}

export const getTotalInvoicevalues = (list) => {
  let editableBilledSum = 0,
    billedSum = 0,
    nonBilledSum = 0,
    totalSum = 0,
    totalAmount = 0,
    totalLogDifference = 0

  list.forEach((record) => {
    if (record?.index !== 'Total') {
      billedSum += parseFloat(record?.billedHours) || 0
      nonBilledSum += parseFloat(record?.nonBilledHours) || 0
      editableBilledSum += record?.editableBilledHours || 0
      totalAmount += record?.amount || 0
      totalSum +=
        record?.calculatedBillingHours +
          record?.extraLoggedHours +
          record?.nonBilledHours || 0
      totalLogDifference +=
        record?.onTimeHoursSum - record?.overAllZohoLogs || 0
    }
  })

  return {
    billedSum,
    nonBilledSum,
    editableBilledSum,
    totalSum,
    totalAmount,
    totalLogDifference,
  }
}

/**
 * This function receives projectDetails and returns the value of validation fields and
 * missing fields while adding prediction and invoice
 */

export const validateRequiredFieldsForPredictions = (projectDetails) => {
  const validationFields = {}
  const missingFields = []

  for (const key in projectDetails) {
    if (Object.keys(requiredFieldsForPrediction).includes(key)) {
      validationFields[key] = projectDetails[key]
    }
  }
  Object.entries(validationFields).forEach((item) => {
    if (item[1] === '-') {
      missingFields.push(requiredFieldsForPrediction[item[0]])
    }
  })
  return { validationFields, missingFields }
}

export const isEmpty = (string) => string?.trim() === ''

// Shared axios `paramsSerializer` for list endpoints whose backend binds
// multi-select filter values as repeated query params (?csmName=A&csmName=B)
// rather than axios's default `a[]=x&a[]=y` array encoding -- also drops
// empty/undefined/null values instead of sending them (an empty param, e.g.
// `?csmName=`, has previously returned zero rows). Used by anomalyServices.js
// and pmProjectServices.js.
export const serializeRepeatedParams = (params) => {
  const searchParams = new URLSearchParams()
  Object.entries(params || {}).forEach(([key, value]) => {
    const values = Array.isArray(value) ? value : [value]
    values
      .filter((v) => v !== undefined && v !== null && v !== '')
      .forEach((v) => searchParams.append(key, v))
  })
  return searchParams.toString()
}

export const debounce = (func, timeout) => {
  let timer
  return (...args) => {
    clearTimeout(timer)
    timer = setTimeout(() => {
      func.apply(this, args)
    }, timeout)
  }
}

export const calculateTotal = (data, type) => {
  switch (type) {
    case 'leave':
      return data?.reduce(
        (total, currentValue) => total + currentValue?.numberOfDays,
        0
      )

    case 'hours':
      return data?.reduce(
        (total, currentValue) =>
          total + parseFloat(currentValue?.totalHours?.toFixed(2)),
        0
      )
    case 'onTime':
      return data?.reduce(
        (total, currentValue) => total + currentValue?.ontimeLogs?.hoursWorked,
        0
      )

    default:
      break
  }
}

/**
 @function mapOptionsByLabelValue

* @description to map select field option by label and value

* @params [options]

* @returns [{label:"abc",value:"id"}]

*

*/
export const mapOptionsByLabelValue = (options) => {
  return options?.map((option) => {
    return {
      label: (
        <div className="option-label-wrapper">
          <div className="option-text">{option?.name}</div>
          {option?.incentiveCreated && (
            <VerifiedIcon style={{ color: '#52c41a', marginLeft: 80 }} />
          )}
        </div>
      ),
      name: option?.name,
      value: option?.id,
    }
  })
}
/**
 @function getCustomerName

* @description return customer name from given companyDetails using first name ,last name etc fields 

* @params {comapnyDetails}

* @returns "customerName" || ''

*

*/
export const getCustomerName = (companyDetails) => {
  if (companyDetails) {
    const name = Object.values(companyDetails)
      .filter(
        (value, index) =>
          value !== null &&
          value !== undefined &&
          booksNameFields.includes(Object.keys(companyDetails)[index])
      )
      .join(' ')
    return name
  }
  return ''
}

export const removeExtraFieldsFromTimesheetData = (
  timesheetData,
  extraKeysArray
) => {
  for (const key in timesheetData) {
    if (extraKeysArray?.includes(key)) delete timesheetData[key]
  }
  return timesheetData
}

export const shallowCopyObject = (originalObj) => {
  return { ...originalObj }
  /**
 @function getCompanyOptionsLabel

* @description return options label with icon based on boolean value of quickbook/zohobooks checkbox

* @params {option}

* @returns <>"string (string) icon"</>

*

*/
}
export const getCompanyOptionsLabel = (option) => {
  return (
    <>
      {`${option?.name} ${option?.region ? `(${option.region})` : ''}`}
      {option.quickbookCheckbox ? <VerifiedIcon /> : null}
    </>
  )
}

export const getMonthsBetween = (startMonth, endMonth) => {
  // Convert start and end months to date objects
  const startDate = new Date(startMonth)
  const endDate = new Date(endMonth)

  // Get the start and end of the month for the interval
  const interval = { start: startDate, end: endDate }

  // Get the months within the interval (inclusive)
  const months = eachMonthOfInterval(interval)

  // Format the months
  const formattedMonths = months.map((month) =>
    format(month, DATE_FORMATS.MMM_YYYY_SHORT)
  )

  return formattedMonths
}
export const calculateGetTimesheetEndDate = (startDate) => {
  const timeSheetStartDate = new Date(startDate)
  // Get today's date
  const today = new Date()

  // Check if today is after the start date
  if (isAfter(today, timeSheetStartDate)) {
    return today
  } else {
    // If today is not after the start date, add 1 day to the start date
    const endDate = addDays(timeSheetStartDate, 1)
    return endDate
  }
}

export const getAccountNameById = (id, accountNameOptions) => {
  const account = accountNameOptions?.find((account) => id === account?.id)
  if (account) {
    return account?.name
  }
}

export const getSubstringBeforeUnderscore = (str) => {
  if (str) {
    const underscoreIndex = str.indexOf('_')
    if (underscoreIndex === -1) {
      return str
    }
    return str.substring(0, underscoreIndex)
  }
}

// Rate Master columns props to filter dates

export const getRateColumnSearchProps = (
  dataIndex,
  dateFrmt,
  picker,
  defaultFilteredValue,
  appliedFilter,
  setAppliedFilter,
  isNaOption = false
) => ({
  filterDropdown: ({
    setSelectedKeys,
    selectedKeys,
    confirm,
    clearFilters,
  }) => (
    <RangeFilterDropdown
      setSelectedKeys={setSelectedKeys}
      selectedKeys={selectedKeys}
      confirm={confirm}
      clearFilters={clearFilters}
      dataIndex={dataIndex}
      dateFrmt={dateFrmt}
      picker={picker}
      defaultFilteredValue={defaultFilteredValue}
      isNaOption={isNaOption}
      appliedFilter={appliedFilter}
      setAppliedFilter={setAppliedFilter}
    />
  ),
  onFilter: undefined,
  filteredValue: defaultFilteredValue?.some((value) => value === null)
    ? null
    : defaultFilteredValue.map((value) =>
        value ? moment(value).format(dateFrmt) : null
      ),
})
const SolidCircle = ({ radius = 10, fillColor }) => (
  <svg width={radius * 2} height={radius * 2}>
    <circle cx={radius} cy={radius} r={radius} fill={fillColor} />
  </svg>
)

export const submissionTypeTitle = (
  <Tooltip
    color="#ffffff"
    overlayClassName="tooltip"
    title={() => (
      <div style={{ padding: '8px' }}>
        <div
          className="color-info"
          style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}
        >
          <SolidCircle radius={10} fillColor="#006064" />
          <span style={{ marginLeft: '8px' }}>Invoice</span>
        </div>
        <div
          className="color-info"
          style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}
        >
          <SolidCircle radius={10} fillColor="#32d670" />
          <span style={{ marginLeft: '8px' }}>Timesheet</span>
        </div>
        <div
          className="color-info"
          style={{ display: 'flex', alignItems: 'center' }}
        >
          <SolidCircle radius={10} fillColor="#F57F17" />
          <span style={{ marginLeft: '8px' }}>Invoice & Timesheet</span>
        </div>
      </div>
    )}
  >
    <span style={{ cursor: 'pointer' }}>Submission Type</span>
  </Tooltip>
)

export const SubmissionTypeIndicator = ({ type }) => {
  const colorConfig = {
    I: '#006064',
    T: '#32d670',
    IT: '#F57F17',
  }

  const typeText = {
    I: 'I',
    T: 'T',
    IT: 'IT',
  }

  const tooltipText = {
    I: 'Invoice',
    T: 'Timesheet',
    IT: 'Invoice & Timesheet',
  }

  const color = colorConfig[type] || '#999'
  const text = typeText[type] || ''
  const tooltip = tooltipText[type] || 'Unknown Type'

  return (
    <Tooltip
      title={tooltip}
      placement="top"
      overlayStyle={{ fontSize: '12px' }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '24px',
          height: '24px',
          borderRadius: '50%',
          backgroundColor: color,
          margin: '0 auto',
          color: 'white',
          fontWeight: 'bold',
          fontSize: '12px',
          cursor: 'pointer',
        }}
      >
        {text}
      </div>
    </Tooltip>
  )
}

export const moveColumnToEnd = (order, keyToMoveLast) => {
  const filtered = order.filter((key) => key !== keyToMoveLast)
  return [...filtered, keyToMoveLast]
}

export const reorderColumnsWithFixedPositions = (order) => {
  const fixedFirst = ['projectName', 'revenueStatus']
  const fixedLast = ['action']

  // Extract fixed first columns that exist in the order
  const actualFixedFirst = fixedFirst.filter((key) => order.includes(key))

  // Extract fixed last columns that exist in the order
  const actualFixedLast = fixedLast.filter((key) => order.includes(key))

  // Get the rest of the columns (excluding fixed positions)
  const rest = order.filter(
    (key) => !fixedFirst.includes(key) && !fixedLast.includes(key)
  )

  return [...actualFixedFirst, ...rest, ...actualFixedLast]
}
